import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";

import { cn, mapFictoanInfoPanelProps } from "@/lib/utils";
import { FictoanInfoPanelProps } from "@/lib/fictoan-schema";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
    return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        data-slot="sheet-overlay"
        className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 setu:fixed setu:inset-0 setu:z-50 setu:bg-black/50",
            className
        )}
        {...props}
        ref={ref}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
    "setu:bg-background data-[state=open]:animate-in data-[state=closed]:animate-out setu:fixed setu:z-50 setu:flex setu:flex-col setu:gap-4 setu:shadow-lg setu:transition setu:ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
    {
        variants: {
            side: {
                top: "setu:inset-x-0 setu:top-0 setu:h-auto setu:!border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
                bottom: "setu:inset-x-0 setu:bottom-0 setu:h-auto setu:!border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
                left: "setu:inset-y-0 setu:left-0 setu:h-full setu:w-3/4 setu:!border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left setu:sm:max-w-sm",
                right: "setu:inset-y-0 setu:right-0 setu:h-full setu:w-3/4 setu:!border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right setu:sm:max-w-sm",
            },
        },
        defaultVariants: {
            side: "right",
        },
    }
);

interface SheetContentProps
    extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
        VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
    ({ side = "right", className, children, ...props }, ref) => (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                data-slot="sheet-content"
                ref={ref}
                className={cn(sheetVariants({ side }), className)}
                {...props}
            >
                {children}
                <SheetPrimitive.Close className="setu:ring-offset-background setu:focus:ring-ring data-[state=open]:bg-secondary setu:absolute setu:top-4 setu:right-4 setu:rounded-xs setu:opacity-70 setu:transition-opacity setu:hover:opacity-100 setu:focus:ring-2 setu:focus:ring-offset-2 setu:focus:outline-hidden setu:disabled:pointer-events-none">
                    <XIcon className="setu:size-4" />
                    <span className="setu:sr-only">Close</span>
                </SheetPrimitive.Close>
            </SheetPrimitive.Content>
        </SheetPortal>
    )
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        data-slot="sheet-header"
        className={cn("setu:flex setu:flex-col setu:gap-1.5 setu:p-4", className)}
        {...props}
    />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        data-slot="sheet-footer"
        className={cn("setu:mt-auto setu:flex setu:flex-col setu:gap-2 setu:p-4", className)}
        {...props}
    />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        data-slot="sheet-title"
        ref={ref}
        className={cn("setu:text-foreground setu:font-semibold", className)}
        {...props}
    />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        data-slot="sheet-description"
        ref={ref}
        className={cn("setu:text-sm setu:text-muted-foreground", className)}
        {...props}
    />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// InfoPanel compatibility wrapper for Fictoan migration
const InfoPanel = React.forwardRef<
    React.ElementRef<typeof SheetContent>,
    FictoanInfoPanelProps & {
        side?: "top" | "right" | "bottom" | "left";
        title?: string;
    }
>(({ side = "right", title, ...fictoanProps }, ref) => {
    // Map Fictoan props to Sheet props
    const { open, onOpenChange, className, ...mappedProps } = mapFictoanInfoPanelProps(fictoanProps);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent ref={ref} side={side} className={className} {...mappedProps}>
                {title && (
                    <SheetHeader>
                        <SheetTitle>{title}</SheetTitle>
                    </SheetHeader>
                )}
                {fictoanProps.children}
            </SheetContent>
        </Sheet>
    );
});
InfoPanel.displayName = "InfoPanel";

export {
    Sheet,
    SheetPortal,
    SheetOverlay,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
    InfoPanel,
};
